import { get, set } from 'firebase/database';
import { tenantRef, logPosEvent } from './posUtil';
import {
  POS_SORT_PREFERENCE_NODE,
  POS_SORT_PREFERENCE_SCREENS,
  POS_PRODUCT_SORTS,
  POS_PRODUCT_SORT_KEYS,
  POS_SORT_ASC,
  POS_SORT_DESC,
  POS_AUDIT_ACTIONS,
} from '../constants/posConst';

// ---- Last chosen sort, per client and screen -------------------------------
//
// Stored at posTenants/<clientId>/sortPreference/<screen> as
// { key, direction, updatedAt }. Per CLIENT rather than per staff member: a
// club arranges its till once and every till there should open the same way.

/** Human names for the audit trail. */
const SCREEN_LABELS = {
  [POS_SORT_PREFERENCE_SCREENS.PRODUCTS]: 'Products',
  [POS_SORT_PREFERENCE_SCREENS.INVENTORY]: 'Inventory',
};

/**
 * Throws unless `screen` is one of POS_SORT_PREFERENCE_SCREENS.
 *
 * @param {string} screen
 * @returns {void}
 * @throws {Error} on an unknown screen — a typo must not create a stray node
 */
const assertScreen = (screen) => {
  if (!Object.values(POS_SORT_PREFERENCE_SCREENS).includes(screen)) {
    throw new Error(`posSortPreferenceUtil: unknown sort screen "${screen}".`);
  }
};

/**
 * Normalises a stored or requested sort into something the screens can use.
 *
 * @param {{key?: string, direction?: string}|null} value
 * @returns {{key: string, direction: string}|null} null when the key is not a
 *   known sort field (an old or hand-edited record)
 */
const cleanSortPreference = (value) => {
  if (!value || !POS_PRODUCT_SORT_KEYS.includes(value.key)) return null;
  const direction = value.direction === POS_SORT_DESC || value.direction === POS_SORT_ASC
    ? value.direction
    : (POS_PRODUCT_SORTS.find((sort) => sort.key === value.key)?.defaultDirection || POS_SORT_ASC);
  return { key: value.key, direction };
};

/**
 * Reads the last saved sort for one screen, once.
 *
 * A one-off read rather than a listener on purpose: another till changing its
 * sort must not reorder the grid under a cashier mid-sale.
 *
 * @param {string} clientId
 * @param {string} screen - a POS_SORT_PREFERENCE_SCREENS value
 * @returns {Promise<{key: string, direction: string}|null>} null when nothing
 *   is saved yet, or the read failed (the screen then stays on its default)
 */
export const readPosSortPreference = async (clientId, screen) => {
  assertScreen(screen);
  try {
    const snapshot = await get(tenantRef(clientId, `${POS_SORT_PREFERENCE_NODE}/${screen}`));
    return cleanSortPreference(snapshot.val());
  } catch (error) {
    console.error('POS sort preference read failed:', error);
    return null;
  }
};

/**
 * Saves one screen's sort as the client's new default, and records it on the
 * audit trail.
 *
 * @param {string} clientId
 * @param {string} screen - a POS_SORT_PREFERENCE_SCREENS value
 * @param {{key: string, direction: string}} sort
 * @param {string} [actor] - the staff address to record
 * @returns {Promise<void>}
 * @throws {Error} when the key is not a known sort field, or the write fails
 */
export const savePosSortPreference = async (clientId, screen, sort, actor = '') => {
  assertScreen(screen);
  const clean = cleanSortPreference(sort);
  if (!clean) throw new Error(`posSortPreferenceUtil: unknown sort field "${sort?.key}".`);

  await set(tenantRef(clientId, `${POS_SORT_PREFERENCE_NODE}/${screen}`), {
    ...clean,
    updatedAt: Date.now(),
  });

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.SORT_PREFERENCE_CHANGED,
    entityId: screen,
    summary: {
      screen: SCREEN_LABELS[screen] || screen,
      sortBy: POS_PRODUCT_SORTS.find((option) => option.key === clean.key)?.label || clean.key,
      direction: clean.direction,
    },
  });
};
